import * as Sentry from "@sentry/react";
import { configureSentryAxiosInterceptor } from "../utils/sentryInterceptor";
import config from "./config";

/**
 * Get Sentry configuration based on environment
 */
const getSentryConfig = () => {
  // Hardcoded DSN from original index.js
  const sentryDSN =
    "https://3983dc579b4d41dcbfa971ee42b2b7a5@o912544.ingest.sentry.io/6508627";

  // Get environment
  const env = process.env.REACT_APP_ENV || "local";

  // Get API URL from config
  const apiUrl = config.apiURL || window.location.origin;

  const zeroSample = env === "testing" || env === "local";
  let sampleRate;
  if (env === "production") {
    sampleRate = 1.0;
  } else if (zeroSample) {
    sampleRate = 0;
  } else {
    sampleRate = 0.1;
  }

  return {
    dsn: zeroSample ? null : sentryDSN,
    environment: env,
    tracesSampleRate: sampleRate,
    integrations: [
      Sentry.browserTracingIntegration({
        // tracingOrigins is deprecated, using tracePropagationTargets instead
        tracePropagationTargets: [window.location.origin, apiUrl, /^\//],
      }),
    ],
    beforeSend(event) {
      // Filter out noise from development
      if (event.message?.includes("Non-Error promise rejection captured")) {
        return null;
      }
      return event;
    },
    beforeSendTransaction(transaction) {
      // The detailing dashboard fires one GET /api/sequences per status tab;
      // the programming dashboard does the same for GET /api/programming.
      // That is intentional but triggers Sentry's N+1 API Call detector
      // (threshold: ≥10 simultaneous calls to the same URL).
      // We cap the number of spans for these routes to 3 so Sentry
      // keeps useful data without flagging a false-positive N+1.
      const N1_ROUTES = ["/api/sequences", "/api/programming"];
      if (transaction.spans) {
        const seenRoutes = {};
        transaction.spans = transaction.spans.filter((span) => {
          if (span.op !== "http.client") return true;

          const raw =
            span.description || span.data?.url || span.data?.["http.url"] || "";
          const basePath = raw.split("?")[0];

          if (!N1_ROUTES.some((route) => basePath.endsWith(route))) {
            return true;
          }

          seenRoutes[basePath] = (seenRoutes[basePath] || 0) + 1;
          return seenRoutes[basePath] <= 3;
        });
      }
      return transaction;
    },
  };
};

/**
 * Initialize Sentry with environment-based configuration
 * Called during app initialization
 * @param {Object} apiClient - Axios instance to configure with Sentry interceptors
 */
export const initializeSentry = (apiClient = null) => {
  try {
    const sentryConfig = getSentryConfig();

    // Only initialize if DSN is provided
    if (!sentryConfig.dsn) {
      console.log("Sentry DSN not provided or disabled for this environment");
      return;
    }

    Sentry.init(sentryConfig);
    console.log("✅ Sentry initialized successfully");

    // Configure axios interceptors after Sentry is initialized
    if (apiClient) {
      configureSentryAxiosInterceptor(apiClient);
      console.log("✅ Sentry axios interceptors configured");
    }
  } catch (error) {
    console.warn("Failed to initialize Sentry:", error);
    // Continue without Sentry rather than breaking the app
  }
};

const sentryConfig = { initializeSentry };
export default sentryConfig;
